import type { ProceduralProfile } from "./AudioProfiles.ts";

/**
 * The pure-math synthesis core shared by every procedural audio voice in the
 * game — SFX (`AudioProfiles.ts`'s 73 profiles), the theme's procedural
 * fallback, and (Task-001, 2026-ingame-music-rework, AC-3) the `"run"`
 * composer's pad/bass/percussion/arp/tension voices.
 *
 * Task-003 (2026-audio-module-audit, AC-4) originally extracted
 * `synthesizeProceduralSamples` verbatim out of (the former body of)
 * `AudioEngine.generateProceduralBuffer` so the loudness-ladder conformance
 * witness (`tests/systems/AudioLoudness.test.ts`) could compute a profile's
 * REAL output samples (RMS/peak) with no `AudioContext` at all. Task-001
 * relocates BOTH functions here (still byte-identical logic) so the new
 * `ProceduralMusic.ts`/`MusicPatterns.ts` composer modules can reuse the
 * exact same synthesis recipe without a circular import against
 * `AudioEngine.ts` (which itself imports `ProceduralMusic` to register the
 * composer) — `AudioEngine.ts` re-exports `synthesizeProceduralSamples` so
 * every existing import site (incl. the loudness witness) is unaffected.
 */
export function synthesizeProceduralSamples(
  profile: ProceduralProfile,
  sampleRate: number,
): Float32Array {
  const length = Math.floor((profile.durationMs / 1000) * sampleRate);
  const data = new Float32Array(length);
  const gain = profile.gain ?? 0.3;

  if (
    profile.type === "square" ||
    profile.type === "sine" ||
    profile.type === "triangle"
  ) {
    const freqStart = profile.freq ?? 440;
    const freqEnd = profile.freqEnd ?? freqStart;
    // S143 / BL-138: a softer envelope on the *frequent* tonals so the attack
    // isn't a clicky step (the click is half the "ping" the owner hated). A
    // short raised-cosine fade-in (~6 ms) takes the edge off without dulling
    // anything that's actually shorter than the fade.
    const attackFadeSamples = Math.min(
      Math.max(1, Math.floor(sampleRate * 0.006)),
      Math.floor(length / 2),
    );
    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const progress = i / length;
      const freq = freqStart + (freqEnd - freqStart) * progress;
      const envelope =
        profile.decay === "exp" ? Math.exp(-5 * progress) : 1 - progress;
      const phase = 2 * Math.PI * freq * t;
      let wave: number;
      if (profile.type === "square") {
        wave = Math.sign(Math.sin(phase));
      } else if (profile.type === "triangle") {
        // (2/π)·asin(sin) — soft odd harmonics, no fizz.
        wave = (2 / Math.PI) * Math.asin(Math.sin(phase));
      } else {
        wave = Math.sin(phase);
      }
      const attack =
        i < attackFadeSamples
          ? 0.5 - 0.5 * Math.cos((Math.PI * i) / attackFadeSamples)
          : 1;
      data[i] = wave * envelope * attack * gain;
    }
  } else if (profile.type === "noise") {
    // BL-120: an optional one-pole low-pass rounds bright white noise into low
    // "weight" (death/leak/overload/detonate) — the per-sample coefficient is
    // a = dt/(RC+dt), RC = 1/(2π·fc). No filter when `lowpass` is unset.
    const lpAlpha =
      profile.lowpass !== undefined
        ? 1 / (sampleRate / (2 * Math.PI * profile.lowpass) + 1)
        : 1;
    let lpPrev = 0;
    for (let i = 0; i < length; i++) {
      const envelope =
        profile.decay === "exp"
          ? Math.exp(-5 * (i / length))
          : 1 - i / length;
      const white = Math.random() * 2 - 1;
      lpPrev = lpPrev + lpAlpha * (white - lpPrev);
      data[i] = lpPrev * envelope * gain;
    }
  } else if (profile.type === "chord") {
    const freqs = profile.freqs ?? [440, 554, 659];
    // S147: a 6 ms raised-cosine onset (same de-click as the tonal branch,
    // S143/BL-138) — kills the chord attack click so the glass UI ticks are soft.
    const attackFadeSamples = Math.min(
      Math.max(1, Math.floor(sampleRate * 0.006)),
      Math.floor(length / 2),
    );
    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const envelope =
        profile.decay === "exp"
          ? Math.exp(-4 * (i / length))
          : 1 - i / length;
      let sum = 0;
      for (const f of freqs) {
        sum += Math.sin(2 * Math.PI * f * t);
      }
      const attack =
        i < attackFadeSamples
          ? 0.5 - 0.5 * Math.cos((Math.PI * i) / attackFadeSamples)
          : 1;
      data[i] = (sum / freqs.length) * envelope * attack * gain;
    }
  }

  return data;
}

/**
 * Wrap `synthesizeProceduralSamples`'s output into a real `AudioBuffer` — the
 * exact byte-for-byte logic `AudioEngine.generateProceduralBuffer` used to
 * inline (Task-003 doc): a BOUNDED copy (not `.set()`, which throws if the
 * destination is shorter than the source), so a fixed-size fake
 * `createBuffer` (the jsdom test stubs, which ignore the requested length)
 * can't throw — a real `AudioContext.createBuffer` always returns exactly
 * `samples.length` frames, so this is a no-op clamp in production.
 *
 * `renderSampleRateHz` (F-1, perf-review finding on 2026-ingame-music-rework
 * Task-001) is an OPTIONAL override for the buffer's own render rate,
 * independent of `ctx.sampleRate` — every existing call site (SFX profiles
 * via `AudioEngine.ts:517`) omits it and is unaffected. It exists so a caller
 * whose content has no high-frequency energy (the composer's long pad/
 * tension voices, `MusicPatterns.ts`'s `LONG_VOICE_RENDER_SAMPLE_RATE_HZ`)
 * can render at a much lower rate and cut that buffer's memory
 * proportionally — `AudioBufferSourceNode` resamples a buffer to the
 * destination context's rate automatically on playback, and `playbackRate`
 * is already relative to the buffer's OWN sample rate, so no pitch/tempo
 * compensation is needed at the call site.
 */
export function synthesizeProceduralBuffer(
  ctx: AudioContext,
  profile: ProceduralProfile,
  renderSampleRateHz?: number,
): AudioBuffer {
  const sampleRate = renderSampleRateHz ?? ctx.sampleRate;
  const samples = synthesizeProceduralSamples(profile, sampleRate);
  const buffer = ctx.createBuffer(1, samples.length, sampleRate);
  const channelData = buffer.getChannelData(0);
  const n = Math.min(samples.length, channelData.length);
  for (let i = 0; i < n; i++) channelData[i] = samples[i]!;
  return buffer;
}
